import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Recommendation } from '../../dto/recommendation';
import { RecommendationService } from '../../service/recommendation.service';
import { Trail } from '../../../trail/dto/trail';
import { RecommendationPropertiesService } from '../../service/recommendation-properties.service';
import { RecommendationType } from '../../dto/recommendation-type';
import { BoolDto } from '../../../../common/dto/bool-dto';
import { RecommendationDetailComponent } from '../recommendation-detail/recommendation-detail.component';
import { RecomWithTestAndStep } from '../../dto/recom-with-test-and-step';
import { Test } from '../../../test/dto/test';
import { R } from '@angular/cdk/keycodes';
import { Router } from '@angular/router';
import { StepRecom } from '../../../step-recom/interface/step-recom';
import { StepRecomService } from '../../../step-recom/service/step-recom.service';
import { TestStep } from '../../../test-step/dto/test-step';
import { TestStepService } from '../../../test-step/service/test-step.service';

@Component({
  selector: 'dpi-recommendation',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatBottomSheetModule, MatButtonToggleModule,
    MatTooltipModule],
  templateUrl: './recommendation.component.html',
  styleUrl: './recommendation.component.scss'
})
export class RecommendationComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['sn', 'description', 'type', 'closed', "testSN", "testName", "stepSN"];
  dataSource: RecomWithTestAndStep[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isWithEditRight: boolean;
  isReadonly: boolean;
  listAllTests: Test[] = [];
  listAllRecommendation: Recommendation[] = [];
  listTestStep: TestStep[] = [];
  listStepRecom: StepRecom[] = [];

  recommendationType: RecommendationType[];
  closedStates: BoolDto[];

  constructor(private notifyService: StateNotifyService
    , private recommendationService: RecommendationService
    , private recommendationPropertiesService: RecommendationPropertiesService
    , private testStepService: TestStepService
    , private stepRecomService: StepRecomService
    , private router: Router) {
    this.isWithEditRight = false;
    this.isReadonly = true;
    this.recommendationType = this.recommendationPropertiesService.getAllRecommendationTypes();
    this.closedStates = this.recommendationPropertiesService.getAllRecommendationClosedStates();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.reload(model);
      });

    this.notifyService.hasEditRight$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isWithEditRight = res;
      });

    this.notifyService.allTestsList$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.listAllTests = model;
    })
  }

  reload(selectedTrail: Trail) {
    this.recommendationService.getAllRecomWithTestAndStepOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var rec of this.dataSource) {
            let rec_type = this.recommendationPropertiesService.getRecommendationType(this.recommendationType, rec.type);
            rec.type_text = rec_type.name;
            let rec_state = this.recommendationPropertiesService.getRecommendationClosedState(this.closedStates, rec.closed);
            rec.closed_text = rec_state.expression;
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  onEditModeChange(value: any) {
    if (value === 'edit') {
      this.isReadonly = false;
    } else {
      this.isReadonly = true;
    }
  }

  selectRecommendation(selectedRecom: RecomWithTestAndStep) {
    if (selectedRecom.test_id > 0) {
      // 1. select the test
      for(var test of this.listAllTests) {
        if(test.id === selectedRecom.test_id) {
          this.notifyService.updateTestSelection(test);
          break;
        }
      }

      if (selectedRecom.step_id && selectedRecom.step_id > 0) {
        // the recommendation is linked to specific test step
        // 2. find the test step and recommendation to the step, and navigate to the step recommendation page
        this.selectStepRecomAndNavigate(selectedRecom.test_id, selectedRecom.step_id, selectedRecom.id);

      } else {
        // the recommendation is linked to a test but not a test step 
        // 2. find the recommendation, select it, and navigate to recommendation page
        this.selectRecommendationAndNavigate(selectedRecom.test_id, selectedRecom.id);
      }
    }
  }

  private selectRecommendationAndNavigate(testId: number, recomId: number) {
    this.recommendationService.getAllRecommendationOfTest(testId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
            this.listAllRecommendation = res;
            this.notifyService.updateListAllRecommendation(this.listAllRecommendation);
            for(var recom of this.listAllRecommendation) {
              if(recom.id === recomId) {
                this.notifyService.updateRecommendationSelection(recom);
                this.router.navigate(['recommendation-hub']);
                break;
              }
            }
        },
        error: (error: any) => {
          console.error('Error: ', error);
        }
      })
  }

  private selectStepRecomAndNavigate(testId: number, stepId: number, recomId: number) {
    this.testStepService.getAllTestStepsOfTest(testId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.listTestStep = res;
          this.notifyService.updateListAllTestStep(this.listTestStep);
          // Find the selected step and its recommendations
          for (var step of this.listTestStep) {
            if (step.id === stepId) {
              this.notifyService.updateTestStepSelection(step);

              this.stepRecomService.getAllStepRecomOfTestStep(stepId)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                  next: (stepRecoms: any) => {
                    this.listStepRecom = stepRecoms;
                    this.notifyService.updateListAllStepRecom(this.listStepRecom);
                    // Find the selected step recommendation
                    for (var recom of this.listStepRecom) {
                      if (recom.id === recomId) {
                        this.notifyService.updateStepRecomSelection(recom);
                        this.router.navigate(['step-recom-hub']);
                        break;
                      }
                    }
                  },
                  error: (error: any) => {
                    console.error('Error: ', error);
                  }
                });

              break;
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
        }
      })

  }


}
